<template>
  <v-card
    :class="['mb-4 telegram-card', { 'telegram-card--active': isConnected }]">
    <v-card-text class="telegram-card-content">
      <div
        :class="[
          'd-flex flex-wrap position-relative',
          isConnected
            ? 'align-start telegram-layout--active'
            : 'align-center telegram-layout--inactive',
        ]">
        <div
          :class="[
            'telegram-icon-box d-flex align-center justify-center flex-shrink-0 position-relative',
            isConnected ? 'telegram-icon-box--active' : '',
          ]">
          <v-icon
            :class="{ 'telegram-icon--inactive': !isConnected }"
            :color="isConnected ? 'white' : '#229ED9'"
            :size="isConnected ? 28 : 32"
            icon="intime:telegram"></v-icon>
          <span
            :class="[
              'telegram-icon-status d-flex align-center justify-center',
              isConnected ? 'telegram-icon-status--active' : '',
            ]">
            <v-icon
              :color="isConnected ? 'white' : '#d99613'"
              :icon="isConnected ? 'mdi-check' : 'mdi-close'"
              :size="isConnected ? 12 : 15"></v-icon>
          </span>
        </div>

        <div class="d-flex flex-column flex-grow-1 telegram-content">
          <div class="d-flex align-center telegram-title-row flex-wrap">
            <span class="font-size-15 font-weight-bold">
              Telegram-бот @{{ botUsername }}
            </span>
            <v-chip
              :class="[
                'telegram-status-chip font-weight-bold text-uppercase',
                isConnected ? 'telegram-status-chip--active' : '',
              ]"
              density="compact"
              rounded="lg"
              size="small"
              variant="flat">
              <span v-if="isConnected" class="telegram-chip-dot"></span>
              {{ isConnected ? 'Активний' : 'Не підключено' }}
            </v-chip>
          </div>

          <div
            v-if="isConnected"
            class="telegram-connected-info d-flex align-center flex-wrap">
            <div class="d-flex align-center telegram-info-item">
              <v-icon color="#229ED9" icon="mdi-account-outline" size="16" />
              <div class="telegram-info-text">
                <div class="telegram-info-label">Акаунт</div>
                <div class="telegram-info-value telegram-info-login">
                  @{{ telegramConnection.login }}
                </div>
              </div>
            </div>

            <v-divider class="telegram-info-divider" vertical></v-divider>

            <div class="d-flex align-center telegram-info-item">
              <v-icon color="#1a7a4a" icon="mdi-clock-outline" size="16" />
              <div class="telegram-info-text">
                <div class="telegram-info-label">Підключено</div>
                <div class="telegram-info-value">
                  {{
                    new CustomDate(telegramConnection.connectedAt).toString({
                      time: true,
                    })
                  }}
                </div>
              </div>
            </div>

            <v-spacer></v-spacer>

            <v-btn
              class="text-none bg-white"
              color="error"
              variant="outlined"
              @click="disconnectTelegram">
              <div class="text-error font-size-12 d-flex align-center ga-1">
                <v-icon>mdi-power-standby</v-icon>
                <span>Від'єднати</span>
              </div>
            </v-btn>
          </div>

          <span
            v-else
            class="text-blue-grey-darken-1 font-size-13 telegram-description">
            Отримуйте миттєві сповіщення про події системи прямо в Telegram.
            Підключення займає менше хвилини.
          </span>
        </div>

        <div v-if="!isConnected" class="d-flex flex-wrap flex-shrink-0">
          <v-btn class="tg-btn" height="42" @click="openConnectDialog">
            <v-progress-circular
              v-if="isWaitingConnection"
              class="mr-2"
              color="white"
              indeterminate
              size="15"
              width="2" />
            <v-icon v-else class="mr-2" icon="intime:telegram" size="15" />
            <span>{{
              isWaitingConnection
                ? 'Очікування підключення...'
                : 'Підключити Telegram'
            }}</span>
          </v-btn>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import { CustomDate } from 'best-modules/utils'
import { useUserCardStore } from '@/dialog/components/user-card/store'
import {
  userConnectTelegram,
  userDisconnectTelegram,
  userTelegramStatus,
} from '@/requests/user'
import { setSnackbar } from 'best-modules/plugins'
import type { UserCardUser } from '@/utils/types/user'

const TELEGRAM_POLL_INTERVAL = 3000
const TELEGRAM_POLL_TIMEOUT = 120000

export default {
  name: 'TelegramIntegration',
  setup() {
    const { user } = useUserCardStore()
    const botUsername =
      import.meta.env.VITE_TELEGRAM_BOT_USERNAME || 'best_leasing_bot'

    const isConnected = computed(
      () => !!user.telegramChatId && !!user.telegramConnectedAt
    )

    const isWaitingConnection = ref(false)
    let pollInterval: ReturnType<typeof setInterval> | null = null
    let pollTimeout: ReturnType<typeof setTimeout> | null = null

    const updateTelegramState = (telegramState: Partial<UserCardUser>) => {
      Object.assign(user, {
        hasTelegramPending:
          telegramState.hasTelegramPending ?? user.hasTelegramPending ?? false,
        telegramChatId: telegramState.telegramChatId ?? null,
        telegramConnectedAt: telegramState.telegramConnectedAt ?? null,
      })
    }

    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval)
        pollInterval = null
      }
      if (pollTimeout) {
        clearTimeout(pollTimeout)
        pollTimeout = null
      }
      isWaitingConnection.value = false
    }

    const startPolling = () => {
      isWaitingConnection.value = true
      pollInterval = setInterval(async () => {
        try {
          const status = await userTelegramStatus(Number(user.id))
          updateTelegramState(status)

          if (status.telegramConnectedAt) {
            stopPolling()
            setSnackbar({ text: 'Telegram успішно підключено!' })
          } else if (!status.hasTelegramPending) {
            stopPolling()
          }
        } catch {
          // ignore polling errors
        }
      }, TELEGRAM_POLL_INTERVAL)

      pollTimeout = setTimeout(() => {
        if (isWaitingConnection.value) {
          stopPolling()
        }
      }, TELEGRAM_POLL_TIMEOUT)
    }

    watch(
      () => user.hasTelegramPending,
      pending => {
        if (pending && !isWaitingConnection.value) {
          startPolling()
        }
      },
      { immediate: true }
    )

    onUnmounted(stopPolling)

    const telegramConnection = computed(() => {
      if (!isConnected.value) return null
      return {
        login: user.telegramChatId,
        connectedAt: user.telegramConnectedAt,
      }
    })

    const openConnectDialog = async () => {
      stopPolling()
      const res = await userConnectTelegram(Number(user.id))
      updateTelegramState(res)

      if (res.telegramDeepLink) {
        window.open(res.telegramDeepLink, '_blank')
      }

      setSnackbar({
        text: 'Перейдіть за посиланням в Telegram та натисніть Start для завершення підключення.',
      })
      startPolling()
    }

    const disconnectTelegram = async () => {
      stopPolling()
      await userDisconnectTelegram(Number(user.id))
      updateTelegramState({
        hasTelegramPending: false,
        telegramChatId: null,
        telegramConnectedAt: null,
      })
      setSnackbar({ text: 'Telegram відключено' })
    }

    return {
      botUsername,
      CustomDate,
      disconnectTelegram,
      isConnected,
      isWaitingConnection,
      openConnectDialog,
      telegramConnection,
    }
  },
}
</script>

<style lang="scss" scoped>
.telegram-card {
  background: #fafbfc;
  border: 1.5px dashed #d0d8e0;
  border-radius: 12px;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.telegram-card--active {
  background: linear-gradient(135deg, #f0f9f4 0%, #f0f6fb 100%);
  border: 1.5px solid #b5dfc7;
  overflow: hidden;
}

.telegram-card-content {
  padding: 18px 20px;
}

.telegram-layout--inactive {
  gap: 18px;
}

.telegram-layout--active {
  gap: 14px;
}

.telegram-icon-box {
  width: 64px;
  height: 64px;
  border: 2px solid #e8e8ed;
  border-radius: 16px;
  background: #ffffff;
}

.telegram-icon-box--active {
  width: 54px;
  height: 54px;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, #37bbfe 0%, #229ed9 100%);
  box-shadow: rgba(34, 158, 217, 0.3) 0 4px 12px;
}

.telegram-icon--inactive {
  opacity: 0.85;
}

.telegram-icon-status {
  position: absolute;
  right: -3px;
  bottom: -3px;
  width: 22px;
  height: 22px;
  border: 2.5px solid #fafbfc;
  border-radius: 50%;
  background: #fff5e6;
}

.telegram-icon-status--active {
  right: -2px;
  bottom: -2px;
  width: 18px;
  height: 18px;
  border: 3px solid #f0f9f4;
  background: #1a7a4a;
}

.telegram-content {
  min-width: 200px;
}

.telegram-title-row {
  gap: 8px;
  margin-bottom: 6px;
}

.telegram-card--active .telegram-title-row {
  margin-bottom: 4px;
}

.telegram-status-chip {
  height: 22px;
  padding: 0 8px;
  border: 1px solid #f4d9a5;
  border-radius: 6px;
  background: #fff5e6 !important;
  color: #c07a00 !important;
  font-size: 10px;
  letter-spacing: 0.04em;
}

.telegram-status-chip--active {
  padding: 0 9px;
  border-color: transparent;
  background: #1a7a4a !important;
  color: #ffffff !important;
}

.telegram-chip-dot {
  width: 5px;
  height: 5px;
  margin-right: 5px;
  border-radius: 50%;
  background: currentColor;
}

.telegram-description {
  line-height: 1.5;
}

.telegram-connected-info {
  gap: 14px;
  padding: 10px 12px;
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  background: #ffffff;
}

.telegram-info-item {
  gap: 8px;
  min-width: 0;
}

.telegram-info-text {
  font-size: 13px;
  line-height: 1.3;
}

.telegram-info-label {
  color: #9898a8;
  font-size: 11px;
}

.telegram-info-value {
  color: #1a1a1b;
  font-weight: 600;
}

.telegram-info-login {
  font-family: monospace;
}

.telegram-info-divider {
  height: 32px;
  border-color: #e8e8ed;
  opacity: 1;
}

.tg-btn {
  gap: 8px;
  padding: 0 18px;
  border-radius: 9px;
  background: linear-gradient(
    135deg,
    rgb(55, 187, 254) 0%,
    rgb(34, 158, 217) 100%
  );
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  box-shadow: rgba(34, 158, 217, 0.3) 0 4px 12px;
  transition: transform 0.15s ease;
}

.tg-btn:hover {
  transform: translateY(-1px);
}
</style>
